import { createAsyncThunk } from '@reduxjs/toolkit';

import { carAPI } from '@/modules/car/api';
import { VehicleCreateForm } from '@/modules/car/model/car-form';
import { namespace } from '@/modules/car/namespace';
import { getCarForm } from '@/modules/car/store/selectors';
import { RootState } from '@/root-store';
import { ThunkResult } from '@/shared/actions/thunk-result';
import { ISubmitFormResult } from '@/shared/models/formSubmit';
import { prepareErrors } from '@/shared/utils/prepareErrors';

import { setCar, updateForm } from '../actions';

type TUpdateCarLicensingForm = Pick<VehicleCreateForm, 'licensingAuthority' | 'licenseNumber'>;

interface IParams {
  carId: string;
  formValues: TUpdateCarLicensingForm;
}

let lastRequestId: string | undefined;

const updateCarLicensing = createAsyncThunk(
  `${namespace}/update-car-licensing`,
  async (
    { carId, formValues }: IParams,
    { getState, dispatch, requestId },
  ): Promise<ThunkResult<ISubmitFormResult<TUpdateCarLicensingForm>>> => {
    lastRequestId = requestId;

    const carForm = getCarForm(getState() as RootState);

    if (!carId || carForm.id !== carId) {
      return { isSucceed: false };
    }

    const [updatedCar, serverValidation] = await carAPI.updateCar({
      ...carForm,
      ...formValues,
    });

    if (lastRequestId !== requestId) {
      return { isReplaced: true };
    }

    if (serverValidation) {
      const { licenseNumber, licensingAuthority, ...other } = serverValidation;

      const result: ISubmitFormResult<TUpdateCarLicensingForm> = {};

      if (licenseNumber || licensingAuthority) {
        result.validationErrors = {
          licenseNumber,
          licensingAuthority,
        };
      }

      const baseErrors = prepareErrors(other)?.filter(Boolean);

      if (baseErrors && baseErrors.length > 0) {
        result.baseErrors = baseErrors;
      }

      return { isSucceed: false, result };
    }

    dispatch(updateForm(updatedCar));
    dispatch(setCar(updatedCar));

    return { isSucceed: true };
  },
);

export type { TUpdateCarLicensingForm };
export { updateCarLicensing };
